import { CommonModule } from '@angular/common';
import {
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { DocumentTypeEnum, FeatureFlagController, OrderEntity } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaDialogConfirmationService } from 'ra-ui';
import { RaIconComponent, RaSquareIconComponent } from 'ra-ui';
import { OrderCreateComponent } from 'src/app/modules/order/components/create/order-create.component';
import {
    RaChatBubbleComponent,
    RaChatComposerComponent,
    RaChatMessageListComponent,
    RaChatOrbComponent,
    RaChatTypingComponent,
} from 'src/app/shared/components/ra-chat';
import { IaChatTypewriterComponent } from './components/ia-chat-typewriter/ia-chat-typewriter.component';
import { IaChatMessage } from './models/ia-chat-message.model';
import { IaChatStore } from './services/ia-chat.store';

@Component({
    selector: 'ia-chat',
    imports: [
        CommonModule,
        TranslatePipe,
        RaIconComponent,
        RaSquareIconComponent,
        RaChatOrbComponent,
        RaChatBubbleComponent,
        RaChatComposerComponent,
        RaChatMessageListComponent,
        RaChatTypingComponent,
        IaChatTypewriterComponent,
        OrderCreateComponent,
    ],
    providers: [IaChatStore],
    templateUrl: './ia-chat.component.html',
    styleUrl: './ia-chat.component.scss',
})
export class IaChatComponent implements OnInit, OnDestroy {
    public orderId = input<string | null>(null);
    /** `true` = destino já definido como documento AVULSO (pula o "onde salvar?"). */
    public standalone = input<boolean>(false);
    /**
     * Texto que a pessoa já escreveu ANTES de abrir o chat (o campo da tela sem
     * seleção). Entra como rascunho, não como mensagem enviada: o chat ainda precisa
     * saber onde salvar, e o `inputLocked` segura o envio até lá.
     */
    public initialDraft = input<string>('');
    public close = output<void>();
    /** Emite o orderId quando o documento foi gerado ('' = documento avulso). */
    public concluded = output<string>();

    public documentTypeEnum = DocumentTypeEnum;

    /** Modal de criar serviço, aberto pelo resolver "onde salvar?". */
    public showCreateOrder = signal(false);

    public store = inject(IaChatStore);

    private _featureFlagController = inject(FeatureFlagController);
    private _dialogConfirmation = inject(RaDialogConfirmationService);
    private _translate = inject(TranslateService);

    /**
     * A home sabe LISTAR avulsos? Só o feed unificado tem a aba "Avulsos"; na
     * lista clássica o `GET /order/filter` exclui as orders pessoais, então um
     * avulso criado aqui não apareceria em tela nenhuma. Nesse caso a opção some
     * do resolver (não é uma escolha do usuário, é uma limitação da tela dele).
     */
    public homeListsStandalone = computed(() => !this._featureFlagController.isBlocked('unified_feed'));

    // Ao concluir com sucesso: avisa a tela de trás (para recarregar) e fecha o
    // modal. `''` = documento avulso (sem order); `null` = ainda não concluiu.
    // Fecha DIRETO (sem a confirmação de saída): não há nada a perder — a
    // conversa virou documento e o rascunho já foi limpo no conclude.
    private _concludedEffect = effect(() => {
        const orderId = this.store.concluded();
        if (orderId === null) return;
        this.concluded.emit(orderId);
        this._animateOut();
    });

    /** `true` enquanto a animação de saída roda — o pai só desmonta depois dela. */
    public closing = signal(false);

    /**
     * ÚNICA porta de saída do chat (✕ e ←): sair DESCARTA a conversa (decisão de
     * produto: todo chat nasce zerado), então com conteúdo do usuário a saída
     * pede confirmação antes de apagar.
     */
    public requestClose(): void {
        if (this.store.isGenerating()) return;

        if (!this.store.hasUserContent()) {
            this.store.discardConversation();
            this._animateOut();
            return;
        }

        this._dialogConfirmation
            .confirm({
                title: this._translate.instant('iaChat.exitTitle'),
                message: this._translate.instant('iaChat.exitMessage'),
                confirmText: this._translate.instant('iaChat.exitConfirm'),
                type: 'warning',
            })
            .subscribe(control => {
                control.close();
                this.store.discardConversation();
                this._animateOut();
            });
    }

    /**
     * Fecha COM animação: como o `@if` do pai destrói o componente no `close`,
     * a saída anima aqui dentro e só então emite. Duração casada com o CSS
     * (`chat-sink-out`).
     */
    private _animateOut(): void {
        if (this.closing()) return;
        this.closing.set(true);
        setTimeout(() => this.close.emit(), 200);
    }

    // ------------------------------------------------------- visualizador de mídia

    /** Visualizador fullscreen: as URLs do balão clicado + o índice em foco. */
    public viewer = signal<{ urls: string[]; index: number } | null>(null);

    /** Grade limitada a 4 miniaturas; o excedente vira o "+N" da última. */
    public static readonly GRID_LIMIT = 4;

    public mediaOf(message: IaChatMessage): string[] {
        return message.mediaUrls.length ? message.mediaUrls : message.mediaPaths;
    }

    public visibleMedia(message: IaChatMessage): string[] {
        return this.mediaOf(message).slice(0, IaChatComponent.GRID_LIMIT);
    }

    public hiddenMediaCount(message: IaChatMessage): number {
        return Math.max(0, this.mediaOf(message).length - IaChatComponent.GRID_LIMIT);
    }

    public openViewer(message: IaChatMessage, index: number): void {
        this.viewer.set({ urls: this.mediaOf(message), index });
    }

    public closeViewer(): void {
        this.viewer.set(null);
    }

    /** Avança/volta com wrap (da última foto o "próxima" volta à primeira). */
    public stepViewer(delta: number): void {
        const viewer = this.viewer();
        if (!viewer) return;
        const total = viewer.urls.length;
        this.viewer.set({ ...viewer, index: (viewer.index + delta + total) % total });
    }

    @HostListener('document:keydown', ['$event'])
    public onViewerKeydown(event: KeyboardEvent): void {
        if (!this.viewer()) return;
        if (event.key === 'Escape') this.closeViewer();
        else if (event.key === 'ArrowRight') this.stepViewer(1);
        else if (event.key === 'ArrowLeft') this.stepViewer(-1);
        else return;
        event.preventDefault();
        event.stopPropagation();
    }

    ngOnInit(): void {
        this.store.init(this.orderId(), this.standalone());

        const draft = this.initialDraft().trim();
        if (draft) this.store.setDraft(draft);
    }

    ngOnDestroy(): void {
        this.store.destroy();
    }

    public onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.store.showAttachMenu.set(false);
        if (input.files) this.store.attachFiles(input.files);
        input.value = '';
    }

    /**
     * Serviço criado dentro do resolver: já conclui o chat nele, para o usuário
     * não ter que reabrir a lista e procurar o que acabou de criar.
     */
    public onServiceCreated(order: OrderEntity): void {
        this.showCreateOrder.set(false);
        if (order?._id) this.store.concludeWithOrder(order._id);
    }

    public openFavoriteFields(): void {
        this.store.showAttachMenu.set(false);
        this.store.loadFavoriteFields();
    }

    /** O composer volta sozinho à altura de uma linha quando o rascunho é limpo. */
    public sendDraft(): void {
        this.store.sendDraft();
    }

}
